import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check,
  Close,
  DownloadOutlined,
  FileCopyOutlined,
  InfoOutlined,
  LockOpenOutlined,
  ZoomOutMapOutlined,
} from "@mui/icons-material";
import copy from "copy-to-clipboard";
import { useTranslation } from "react-i18next";

const REDBOX_RED = "#F0341A";
export const REDBOX_DROP_OFF_CARD_SRC = "/static/redbox/self-drop-off-card.jpeg";
const REDBOX_DROP_OFF_CARD_FILE_NAME = "redbox-self-drop-off-card.jpeg";
// Fixed panel height: the drop-off card thumbnail stretches to fill it.
const PANEL_HEIGHT = 168;
const CARD_WIDTH = 104;

/**
 * RedBox self drop-off block shown inside the shipment details card:
 * the locker unlock code (copyable) + the merchant self drop-off card
 * (previewable / downloadable).
 */
export default function RedboxUnlockCodeSection({ unlockCode }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleCopy = () => {
    copy(unlockCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          width: "100%",
          height: { xs: "auto", md: PANEL_HEIGHT },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "stretch" },
          gap: 2,
          borderColor: "rgba(240, 52, 26, 0.35)",
          borderInlineStartWidth: 4,
          borderInlineStartColor: REDBOX_RED,
          backgroundColor: "rgba(240, 52, 26, 0.04)",
        }}
      >
        {/* Title + unlock code */}
        <Stack
          spacing={1}
          justifyContent="center"
          sx={{ flex: 1, minWidth: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <LockOpenOutlined sx={{ color: REDBOX_RED }} fontSize="small" />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ color: REDBOX_RED }}
            >
              {t("RedBox self drop-off")}
            </Typography>
            <Tooltip
              title={t(
                "Scan the card at the RedBox locker screen, then enter the unlock code to open the box and drop off your shipment.",
              )}
            >
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Stack>

          <Typography variant="body2" color="text.lighter" fontWeight="bold">
            {t("Redbox box unlock code")}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                px: 2,
                py: 1,
                borderRadius: 1,
                border: "1px dashed",
                borderColor: "rgba(240, 52, 26, 0.5)",
                backgroundColor: "background.paper",
              }}
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                dir="ltr"
                sx={{
                  fontFamily: "monospace",
                  letterSpacing: 4,
                  color: "black",
                }}
              >
                {unlockCode}
              </Typography>
            </Box>
            <Tooltip title={copied ? t("Copied") : t("Copy")}>
              <IconButton size="small" onClick={handleCopy}>
                {copied ? (
                  <Check fontSize="small" color="success" />
                ) : (
                  <FileCopyOutlined fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Self drop-off card */}
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: { xs: "center", md: "stretch" }, flexShrink: 0 }}
        >
          <Box
            onClick={() => setPreviewOpen(true)}
            sx={{
              position: "relative",
              width: { xs: 96, md: CARD_WIDTH },
              height: { xs: 96, md: "100%" },
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              cursor: "pointer",
              flexShrink: 0,
              "&:hover .redbox-card-overlay": { opacity: 1 },
            }}
          >
            <Box
              component="img"
              src={REDBOX_DROP_OFF_CARD_SRC}
              alt={t("RedBox self drop-off card")}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <Box
              className="redbox-card-overlay"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0, 0, 0, 0.45)",
                color: "#fff",
                opacity: 0,
                transition: "opacity 0.2s",
              }}
            >
              <ZoomOutMapOutlined fontSize="small" />
            </Box>
          </Box>

          <Stack spacing={1} justifyContent="center">
            <Typography variant="body2" fontWeight="bold" color="black">
              {t("RedBox self drop-off card")}
            </Typography>
            <Button
              component="a"
              href={REDBOX_DROP_OFF_CARD_SRC}
              download={REDBOX_DROP_OFF_CARD_FILE_NAME}
              size="small"
              variant="contained"
              startIcon={<DownloadOutlined />}
              sx={{
                backgroundColor: REDBOX_RED,
                "&:hover": { backgroundColor: "#c92a15" },
              }}
            >
              {t("Download card")}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography variant="h6" fontWeight="bold">
              {t("RedBox self drop-off card")}
            </Typography>
            <IconButton size="small" onClick={() => setPreviewOpen(false)}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
          <Box
            component="img"
            src={REDBOX_DROP_OFF_CARD_SRC}
            alt={t("RedBox self drop-off card")}
            sx={{ width: "100%", borderRadius: 1, display: "block" }}
          />
          <Button
            component="a"
            href={REDBOX_DROP_OFF_CARD_SRC}
            download={REDBOX_DROP_OFF_CARD_FILE_NAME}
            fullWidth
            variant="contained"
            startIcon={<DownloadOutlined />}
            sx={{
              mt: 2,
              backgroundColor: REDBOX_RED,
              "&:hover": { backgroundColor: "#c92a15" },
            }}
          >
            {t("Download card")}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

RedboxUnlockCodeSection.propTypes = {
  unlockCode: PropTypes.string.isRequired,
};
